import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Loader2, Check, UserRound, Upload, Link2, Linkedin, BadgeCheck, Trash2, Plus, Sparkles, Building2, Search, X, Briefcase, PhoneIncoming, PhoneOutgoing, PhoneMissed, Users, StickyNote, FileAudio, ChevronDown, AlertTriangle } from 'lucide-react';
import { splitFullName } from '../lib/nameUtils';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { title: '', first_name: '', last_name: '', company: '', private_address: '', email: '', email_private: '', phone_work: '', phone_private: '', website: '', linkedin_url: '', type: '', notes: '' };

const fmtDuration = (s) => `${Math.floor((s || 0) / 60)}:${String((s || 0) % 60).padStart(2, '0')} min`;
const fmtDate = (at) => {
  if (!at) return '—';
  const d = new Date(at);
  if (isNaN(d)) return at;
  const dateStr = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return at.length > 10 ? `${dateStr} ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}` : dateStr;
};

function EngagementIcon({ e }) {
  if (e.kind === 'call') {
    if (e.direction === 'in' && !e.answered) return <PhoneMissed className="w-3.5 h-3.5 text-red-400 shrink-0" />;
    if (e.direction === 'in') return <PhoneIncoming className="w-3.5 h-3.5 text-emerald-400 shrink-0" />;
    return <PhoneOutgoing className="w-3.5 h-3.5 text-sky-400 shrink-0" />;
  }
  if (e.kind === 'meeting') return <Users className="w-3.5 h-3.5 text-violet-400 shrink-0" />;
  return <StickyNote className="w-3.5 h-3.5 text-muted-foreground shrink-0" />;
}

function EngagementHistory({ contactId, onOpenChange }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openPlaud, setOpenPlaud] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.getContactEngagements(contactId)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [contactId]);

  const rows = data?.engagements || [];
  return (
    <div className="border border-border rounded-sm p-3 bg-secondary/20" data-testid="contact-engagements-section">
      <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-2">
        Calls &amp; Meetings
        {data && (rows.length > 0) && (
          <span className="ml-1.5 normal-case tracking-normal text-muted-foreground">
            — {data.counts.calls} call{data.counts.calls !== 1 ? 's' : ''} · {data.counts.meetings} meeting{data.counts.meetings !== 1 ? 's' : ''}
          </span>
        )}
      </p>
      {loading ? (
        <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">No calls or meetings tracked yet — Webex calls are matched via the phone numbers above, meetings via deal events.</p>
      ) : (
        <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
          {rows.map((e, i) => (
            <div key={e.id || i} className="flex items-start gap-2 px-2 py-1.5 bg-secondary/40 border border-border/60 rounded-sm"
              data-testid={`contact-engagement-${i}`}>
              <span className="mt-0.5"><EngagementIcon e={e} /></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs text-foreground">
                    {e.kind === 'call'
                      ? `Call ${e.direction === 'in' ? '(incoming' : '(outgoing'}${e.answered ? '' : ' — not answered'})`
                      : (e.title || (e.kind === 'meeting' ? 'Meeting' : 'Note'))}
                  </span>
                  {e.kind === 'call' && e.duration_s > 0 && (
                    <span className="text-[10px] font-mono text-muted-foreground">{fmtDuration(e.duration_s)}</span>
                  )}
                  {e.source === 'webex' && (
                    <span className="text-[9px] font-mono border border-sky-600/40 bg-sky-600/10 text-sky-300 rounded px-1 py-0">Webex</span>
                  )}
                </div>
                {e.notes && <p className="text-[11px] text-muted-foreground truncate" title={e.notes}>{e.notes}</p>}
                {e.plaud && (
                  <>
                    <button onClick={() => setOpenPlaud(openPlaud === i ? null : i)}
                      data-testid={`contact-engagement-plaud-${i}`}
                      className="flex items-center gap-1 mt-0.5 text-[10px] text-violet-300 hover:text-violet-200 transition-colors max-w-full">
                      <FileAudio className="w-3 h-3 shrink-0" />
                      <span className="truncate">{e.plaud.title}</span>
                      <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${openPlaud === i ? 'rotate-180' : ''}`} />
                    </button>
                    {openPlaud === i && (
                      <p className="text-[11px] text-foreground/85 whitespace-pre-wrap mt-1 border border-violet-500/25 bg-violet-500/5 rounded-sm p-2 max-h-40 overflow-y-auto"
                        data-testid={`contact-engagement-plaud-summary-${i}`}>
                        {e.plaud.summary || '—'}
                      </p>
                    )}
                  </>
                )}
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] font-mono text-muted-foreground">{fmtDate(e.at)}</span>
                  {e.project_name && (
                    <button onClick={() => { onOpenChange(false); navigate(`/projects/${e.project_id}`); }}
                      data-testid={`contact-engagement-deal-${i}`}
                      className="text-[10px] text-sky-400 hover:underline truncate max-w-[200px]">
                      {e.project_name}
                    </button>
                  )}
                  {e.created_by && <span className="text-[10px] text-muted-foreground/70 truncate">by {e.created_by}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ContactDialog({ open, onOpenChange, contact, types, isAdmin, onSaved, projectId = '', defaultCompany = '', defaultType = '' }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoInfo, setPhotoInfo] = useState(null);
  const [photoBust, setPhotoBust] = useState(0);
  const [urlMode, setUrlMode] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  const [newType, setNewType] = useState('');
  const [addingType, setAddingType] = useState(false);
  const [typeList, setTypeList] = useState(types || []);
  const [candidates, setCandidates] = useState(null);
  const [searchingPhotos, setSearchingPhotos] = useState(false);
  const [lookup, setLookup] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [linkedDeals, setLinkedDeals] = useState([]);
  const [dealSearch, setDealSearch] = useState('');
  const [dealResults, setDealResults] = useState([]);
  const [apolloBusy, setApolloBusy] = useState(false);
  const [apolloResult, setApolloResult] = useState(null);
  const [apolloApplying, setApolloApplying] = useState(false);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneResult, setPhoneResult] = useState(null);
  const [phoneApplying, setPhoneApplying] = useState(false);
  const [dupMatches, setDupMatches] = useState(null);
  const [linking, setLinking] = useState('');
  const pollRef = useRef(null);
  const fileRef = useRef(null);
  const { user } = useAuth();
  const canAssignBroker = (user?.email || '').toLowerCase() === 'talu@olta-nachfolge.com';

  useEffect(() => { setTypeList(types || []); }, [types]);
  useEffect(() => {
    if (!open) return;
    setForm(contact ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map(k => [k, contact[k] || ''])) } : { ...EMPTY, company: defaultCompany, type: defaultType });
    setPhotoInfo(contact ? { has_photo: contact.has_photo, photo_verified: contact.photo_verified, photo_verified_by: contact.photo_verified_by } : null);
    setUrlMode(false); setPhotoUrl(''); setPhotoBust(Date.now());
    setCandidates(null); setSearchingPhotos(false);
    setLookup(null); setDealSearch(''); setDealResults([]);
    setApolloResult(null); setApolloBusy(false);
    setDupMatches(null);
    setPhoneResult(contact?.apollo_phone && contact.apollo_phone.status !== 'pending' ? contact.apollo_phone : null);
    setPhoneBusy(false);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    const ids = (contact?.project_ids || []).concat(!contact && projectId ? [projectId] : []);
    if (ids.length) {
      api.getDealsLite({ ids: ids.join(',') })
        .then(r => setLinkedDeals(r.data.deals || []))
        .catch(() => setLinkedDeals(ids.map(id => ({ id, name: 'Deal', stage_label: '' }))));
    } else {
      setLinkedDeals([]);
    }
  }, [open, contact, projectId, defaultCompany, defaultType]);

  // company-first check: known company? existing contacts, matching deals + their shareholders
  useEffect(() => {
    if (!open) return;
    const q = form.company.trim();
    if (q.length < 3) { setLookup(null); return; }
    const t = setTimeout(() => {
      setLookupBusy(true);
      api.companyLookup(q)
        .then(r => setLookup(r.data))
        .catch(() => setLookup(null))
        .finally(() => setLookupBusy(false));
    }, 400);
    return () => clearTimeout(t);
  }, [form.company, open]);

  // deal search for manual linking
  useEffect(() => {
    if (!open || !dealSearch.trim()) { setDealResults([]); return; }
    const t = setTimeout(() => {
      api.getDealsLite({ q: dealSearch.trim() })
        .then(r => setDealResults((r.data.deals || []).filter(d => !linkedDeals.some(l => l.id === d.id))))
        .catch(() => setDealResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [dealSearch, open, linkedDeals]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const addDeal = (d) => { setLinkedDeals(l => l.some(x => x.id === d.id) ? l : [...l, d]); setDealSearch(''); setDealResults([]); };
  const removeDeal = (id) => setLinkedDeals(l => l.filter(d => d.id !== id));

  const pickShareholder = (sh) => {
    const name = typeof sh === 'string' ? sh : sh.name;
    const age = (typeof sh === 'object' && sh.age) ? String(sh.age) : '';
    const { first, last } = splitFullName(name);
    setForm(f => {
      const ownerLabel = typeList.find(t => t.label.toLowerCase() === 'owner')?.label || 'Owner';
      const noteLine = age ? `Alter: ${age} (aus Deal-Gesellschafterdaten)` : '';
      const notes = noteLine && !(f.notes || '').includes(noteLine)
        ? (f.notes ? `${f.notes}\n${noteLine}` : noteLine)
        : (f.notes || '');
      return { ...f, first_name: first, last_name: last, type: f.type || ownerLabel, notes };
    });
  };

  // reset the same-name warning when the identifying fields change
  useEffect(() => { setDupMatches(null); }, [form.first_name, form.last_name, form.email, form.phone_work, form.phone_private]);

  // Instead of a second record for the same person: attach the existing contact to the selected deal(s).
  const linkExisting = async (m) => {
    setLinking(m.id);
    try {
      for (const d of linkedDeals) await api.linkContactToProject(m.id, d.id);
      toast.success(`${m.name} mit ${linkedDeals.length === 1 ? linkedDeals[0].name : `${linkedDeals.length} Deals`} verknüpft — kein neuer Kontakt angelegt`);
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Verknüpfen fehlgeschlagen'); }
    setLinking('');
  };

  const save = async () => {
    if (!form.last_name.trim() && !form.company.trim()) { toast.error('Last name or company required'); return; }
    if (!form.type.trim()) { toast.error('Please select a contact type'); return; }
    setSaving(true);
    try {
      // near-duplicate check for NEW contacts — warn before creating (Max, 31.08.2026 / 15.10.2026)
      if (!contact?.id && dupMatches === null && (form.last_name.trim() || form.email.trim())) {
        const r = await api.contactNameCheck(form.first_name.trim(), form.last_name.trim(), form.email.trim(), form.phone_work.trim() || form.phone_private.trim());
        if ((r.data.matches || []).length) { setDupMatches(r.data.matches); setSaving(false); return; }
      }
      const payload = { ...form, project_ids: linkedDeals.map(d => d.id) };
      if (contact?.id) await api.updateContact(contact.id, payload);
      else await api.createContact({ ...payload, force: dupMatches !== null });
      toast.success('Contact saved');
      onSaved();
      onOpenChange(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.matches) { setDupMatches(d.matches); }
      else toast.error((typeof d === 'string' ? d : d?.message) || 'Failed to save');
    }
    setSaving(false);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      const r = await api.uploadContactPhoto(contact.id, file);
      setPhotoInfo(r.data); setPhotoBust(Date.now());
      toast.success('Photo uploaded — awaiting verification');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setPhotoBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const importFromUrl = async () => {
    if (!photoUrl.trim()) return;
    setPhotoBusy(true);
    try {
      const r = await api.contactPhotoFromUrl(contact.id, photoUrl.trim());
      setPhotoInfo(r.data); setPhotoBust(Date.now()); setUrlMode(false); setPhotoUrl('');
      toast.success('Photo imported — awaiting verification');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    setPhotoBusy(false);
  };

  const verifyPhoto = async () => {
    try {
      const r = await api.verifyContactPhoto(contact.id);
      setPhotoInfo(p => ({ ...p, photo_verified: true, photo_verified_by: r.data.verified_by }));
      toast.success('Photo verified');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deletePhoto = async () => {
    try {
      await api.deleteContactPhoto(contact.id);
      setPhotoInfo({ has_photo: false });
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const addType = async () => {
    if (!newType.trim()) return;
    try {
      const r = await api.addContactType(newType.trim());
      setTypeList(r.data);
      setForm(f => ({ ...f, type: newType.trim() }));
      setNewType(''); setAddingType(false);
      toast.success('Type added');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const linkedinSearch = () => {
    const q = encodeURIComponent([form.first_name, form.last_name, form.company].filter(Boolean).join(' '));
    window.open(`https://www.linkedin.com/search/results/people/?keywords=${q}`, '_blank');
  };

  const apolloEnrich = async () => {
    if (!contact?.id) { toast.error('Save the contact first, then run the Apollo lookup'); return; }
    if (!form.last_name.trim() || !(form.company.trim() || form.website.trim() || form.linkedin_url.trim())) {
      toast.error('Last name + company (or website/LinkedIn) needed for the Apollo lookup');
      return;
    }
    setApolloBusy(true);
    setApolloResult(null);
    try {
      const r = await api.apolloEnrichContact(contact.id, {
        first_name: form.first_name.trim(), last_name: form.last_name.trim(),
        company: form.company.trim(), domain: form.website.trim(), linkedin_url: form.linkedin_url.trim(),
      });
      setApolloResult(r.data);
      if (!r.data.found && !r.data.linkedin_url) {
        toast.info(r.data.email_status === 'locked'
          ? 'Apollo found the person but the email is locked (no email credits on your Apollo plan)'
          : 'Apollo found no match for this person (0 credits used)');
      } else if (!r.data.found && r.data.linkedin_url) {
        toast.info('No email found, but Apollo matched the LinkedIn profile — see below');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Apollo lookup failed'); }
    setApolloBusy(false);
  };

  const apolloFindPhone = async () => {
    if (!contact?.id) { toast.error('Save the contact first, then run the Apollo lookup'); return; }
    if (!form.last_name.trim() || !(form.company.trim() || form.website.trim() || form.linkedin_url.trim())) {
      toast.error('Last name + company (or website/LinkedIn) needed for the Apollo lookup');
      return;
    }
    setPhoneBusy(true);
    setPhoneResult(null);
    try {
      await api.apolloFindPhone(contact.id, {
        origin: window.location.origin,
        first_name: form.first_name.trim(), last_name: form.last_name.trim(),
        company: form.company.trim(), domain: form.website.trim(), linkedin_url: form.linkedin_url.trim(),
      });
      setPhoneResult({ status: 'pending', numbers: [] });
      let tries = 0;
      pollRef.current = setInterval(async () => {
        tries += 1;
        try {
          const r = await api.apolloPhoneStatus(contact.id);
          if (r.data.status === 'received') {
            clearInterval(pollRef.current); pollRef.current = null;
            setPhoneResult(r.data);
            setPhoneBusy(false);
            toast[r.data.numbers?.length ? 'success' : 'info'](r.data.numbers?.length
              ? `Apollo found ${r.data.numbers.length} phone number${r.data.numbers.length !== 1 ? 's' : ''}`
              : 'Apollo found no phone number for this person');
          }
        } catch { /* keep polling */ }
        if (tries >= 30 && pollRef.current) {
          clearInterval(pollRef.current); pollRef.current = null;
          setPhoneBusy(false);
          toast.info('Apollo has not delivered the number yet — reopen the contact in a minute to check again.');
        }
      }, 3000);
    } catch (err) {
      setPhoneBusy(false);
      toast.error(err.response?.data?.detail || 'Apollo phone lookup failed');
    }
  };

  const apolloApplyPhone = async (number) => {
    setPhoneApplying(true);
    try {
      await api.apolloApplyPhone(contact.id, number);
      setForm(f => ({ ...f, phone_private: number }));
      toast.success('Phone number applied as private number');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to apply'); }
    setPhoneApplying(false);
  };

  const apolloApplyLinkedinFn = async () => {
    setApolloApplying(true);
    try {
      const r = await api.apolloApplyLinkedin(contact.id);
      setForm(f => ({ ...f, linkedin_url: r.data.linkedin_url }));
      toast.success('LinkedIn profile applied to the contact');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to apply'); }
    setApolloApplying(false);
  };

  const apolloApply = async () => {
    setApolloApplying(true);
    try {
      const r = await api.apolloApplyEmail(contact.id);
      setForm(f => ({ ...f, email: r.data.email }));
      setApolloResult(res => (res ? { ...res, applied: true } : res));
      toast.success('Apollo email applied to the contact');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to apply'); }
    setApolloApplying(false);
  };

  const findPhotos = async () => {
    const fullName = [form.first_name, form.last_name].filter(Boolean).join(' ').trim();
    if (!fullName) { toast.error('Enter a name first'); return; }
    setSearchingPhotos(true);
    setCandidates(null);
    try {
      const r = await api.searchContactPhotos(fullName, form.company.trim());
      setCandidates(r.data.candidates || []);
      if (!(r.data.candidates || []).length) toast.info('No photos found — try the manual LinkedIn search');
    } catch (err) { toast.error(err.response?.data?.detail || 'Photo search failed'); }
    setSearchingPhotos(false);
  };

  const pickCandidate = async (c) => {
    setPhotoBusy(true);
    try {
      const r = await api.contactPhotoFromUrl(contact.id, c.image, c.thumbnail, 'search');
      setPhotoInfo(r.data); setPhotoBust(Date.now()); setCandidates(null);
      toast.success('Photo saved — awaiting verification');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save this photo — try another one'); }
    setPhotoBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[88vh] flex flex-col" data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground">
            {contact?.id ? 'Edit Contact' : 'New Contact'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {/* Company first — checks whether we already know this company */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Company *</p>
              <div className="relative">
                <Building2 className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input value={form.company} onChange={set('company')} autoFocus={!contact?.id}
                  placeholder="Start with the company…" data-testid="contact-company-input"
                  className="pl-7 bg-secondary border-border text-foreground rounded-sm h-8 text-sm" />
                {lookupBusy && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-muted-foreground" />}
              </div>
            </div>
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Type *</p>
              <div className="flex gap-1.5">
                <select value={form.type} onChange={set('type')} data-testid="contact-type-select"
                  className="flex-1 text-sm bg-secondary border border-border text-foreground rounded-sm px-2 h-8 cursor-pointer focus:outline-none">
                  <option value="">— select type —</option>
                  {typeList.filter(t => canAssignBroker || form.type === t.label || (t.label || '').toLowerCase() !== 'broker')
                    .map(t => <option key={t.id} value={t.label}>{t.label}</option>)}
                </select>
                {isAdmin && !addingType && (
                  <Button variant="outline" size="sm" onClick={() => setAddingType(true)} title="Add new type"
                    data-testid="contact-add-type-btn"
                    className="border-border text-muted-foreground rounded-sm h-8 w-8 p-0"><Plus className="w-3.5 h-3.5" /></Button>
                )}
              </div>
              {addingType && (
                <div className="flex gap-1.5 mt-1.5">
                  <Input value={newType} onChange={e => setNewType(e.target.value)} placeholder="New type…" autoFocus
                    data-testid="contact-new-type-input"
                    onKeyDown={e => e.key === 'Enter' && addType()}
                    className="bg-secondary border-border text-foreground rounded-sm h-7 text-xs" />
                  <Button size="sm" onClick={addType} className="bg-violet-700 hover:bg-violet-600 text-white rounded-sm h-7 text-xs px-2">Add</Button>
                </div>
              )}
            </div>
          </div>

          {/* Known-company panel: matching deals, shareholders/management, existing contacts */}
          {lookup && (lookup.deals.length > 0 || lookup.contacts.length > 0) && (
            <div className="border border-sky-600/30 bg-sky-600/5 rounded-sm p-3 space-y-2" data-testid="company-lookup-panel">
              {lookup.deals.length > 0 && (
                <div>
                  <p className="text-[10px] text-sky-400/80 uppercase tracking-wider mb-1">Company on file — matching deals (click to link)</p>
                  <div className="flex flex-wrap gap-1.5">
                    {lookup.deals.map((d, i) => {
                      const linked = linkedDeals.some(l => l.id === d.id);
                      return (
                        <button key={d.id} onClick={() => linked ? removeDeal(d.id) : addDeal(d)}
                          data-testid={`lookup-deal-${i}`}
                          className={`flex items-center gap-1 text-[10px] font-mono border rounded px-1.5 py-0.5 transition-colors ${linked ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-300' : 'border-border bg-secondary/40 text-foreground/70 hover:border-sky-500/60'}`}>
                          {linked && <Check className="w-2.5 h-2.5" />}
                          {d.name}
                          {d.stage_label && <span className="text-muted-foreground">· {d.stage_label}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {lookup.deals.some(d => (d.shareholders || []).length > 0) && (
                <div>
                  <p className="text-[10px] text-sky-400/80 uppercase tracking-wider mb-1">Known shareholders (click to prefill name)</p>
                  <div className="flex flex-wrap gap-1.5">
                    {lookup.deals.flatMap((d) => (d.shareholders || []).map(sh => {
                      const name = typeof sh === 'string' ? sh : sh.name;
                      const pct = typeof sh === 'object' && sh.pct != null && sh.pct !== '' ? sh.pct : null;
                      const age = typeof sh === 'object' && sh.age ? sh.age : null;
                      return (
                        <button key={`${d.id}-${name}`} onClick={() => pickShareholder(sh)}
                          data-testid="lookup-shareholder"
                          className="flex items-center gap-1 text-[10px] border border-border bg-secondary/40 text-foreground/80 rounded px-1.5 py-0.5 hover:border-emerald-500/60 transition-colors">
                          <UserRound className="w-2.5 h-2.5" /> {name}
                          {pct != null && <span className="font-mono text-emerald-400">{pct}%</span>}
                          {age && <span className="text-muted-foreground">· {age}</span>}
                        </button>
                      );
                    }))}
                  </div>
                </div>
              )}
              {lookup.deals.some(d => d.management_team) && (
                <div>
                  <p className="text-[10px] text-sky-400/80 uppercase tracking-wider mb-0.5">Management team (from deal notes)</p>
                  {lookup.deals.filter(d => d.management_team).map(d => (
                    <p key={d.id} className="text-[10px] text-muted-foreground line-clamp-2">{d.management_team}</p>
                  ))}
                </div>
              )}
              {lookup.contacts.length > 0 && (
                <p className="text-[10px] text-amber-400" data-testid="lookup-existing-contacts">
                  Already on file for this company: {lookup.contacts.map(c => [c.first_name, c.last_name].filter(Boolean).join(' ')).join(', ')}
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-4 gap-2">
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Title</p>
              <Input value={form.title} onChange={set('title')} placeholder="Dr." data-testid="contact-title-input"
                className="bg-secondary border-border text-foreground rounded-sm h-8 text-sm" />
            </div>
            <div className="col-span-1">
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">First Name</p>
              <Input value={form.first_name} onChange={set('first_name')} data-testid="contact-firstname-input"
                className="bg-secondary border-border text-foreground rounded-sm h-8 text-sm" />
            </div>
            <div className="col-span-2">
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Last Name *</p>
              <Input value={form.last_name} onChange={set('last_name')} data-testid="contact-lastname-input"
                className="bg-secondary border-border text-foreground rounded-sm h-8 text-sm" />
            </div>
          </div>
          <div>
            <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Private Address</p>
            <Input value={form.private_address} onChange={set('private_address')} placeholder="Street, ZIP City"
              data-testid="contact-address-input"
              className="bg-secondary border-border text-foreground rounded-sm h-8 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                Email
                {contact?.apollo?.applied && contact?.apollo?.email && form.email === contact.apollo.email && (
                  <span data-testid="apollo-email-badge"
                    className={`inline-flex items-center gap-0.5 normal-case tracking-normal text-[9px] font-mono border rounded px-1 py-0 ${contact.apollo.email_status === 'verified' ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-amber-500/50 bg-amber-500/10 text-amber-300'}`}>
                    {contact.apollo.email_status === 'verified' ? '✓ Verified (Apollo)' : `~ ${contact.apollo.email_status || 'guessed'} (Apollo)`}
                  </span>
                )}
              </p>
              <div className="flex gap-1.5">
                <Input value={form.email} onChange={set('email')} type="email" data-testid="contact-email-input"
                  className="flex-1 bg-secondary border-border text-foreground rounded-sm h-8 text-sm" />
                <Button size="sm" onClick={apolloEnrich} disabled={apolloBusy} title="Find the business email via Apollo.io"
                  data-testid="apollo-enrich-btn"
                  className="bg-orange-700 hover:bg-orange-600 text-white rounded-sm h-8 text-[11px] px-2 shrink-0">
                  {apolloBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
                  Apollo
                </Button>
              </div>
            </div>
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Email (Private)</p>
              <Input value={form.email_private} onChange={set('email_private')} type="email" data-testid="contact-emailprivate-input"
                className="bg-secondary border-border text-foreground rounded-sm h-8 text-sm" />
            </div>
          </div>
          {apolloResult?.found && (
            <div className="border border-orange-500/30 bg-orange-500/5 rounded-sm p-2.5" data-testid="apollo-result-box">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground font-mono">{apolloResult.email}</span>
                <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 ${apolloResult.email_status === 'verified' ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-amber-500/50 bg-amber-500/10 text-amber-300'}`}
                  data-testid="apollo-status-badge">
                  {apolloResult.email_status === 'verified' ? '✓ Verified' : `~ ${apolloResult.email_status || 'guessed'}`}
                </span>
                {(apolloResult.person_title || apolloResult.organization) && (
                  <span className="text-[10px] text-muted-foreground truncate">
                    {[apolloResult.person_title, apolloResult.organization].filter(Boolean).join(' · ')}
                  </span>
                )}
                {apolloResult.applied || form.email === apolloResult.email ? (
                  <span className="ml-auto text-[10px] text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> Applied</span>
                ) : (
                  <Button size="sm" onClick={apolloApply} disabled={apolloApplying} data-testid="apollo-apply-btn"
                    className="ml-auto bg-emerald-700 hover:bg-emerald-600 text-white rounded-sm h-6 text-[10px] px-2">
                    {apolloApplying ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply email'}
                  </Button>
                )}
              </div>
              {form.email && form.email !== apolloResult.email && (
                <p className="text-[10px] text-amber-300 mt-1">Current email stays untouched — click "Apply email" to replace it.</p>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Phone (Work)</p>
              <Input value={form.phone_work} onChange={set('phone_work')} data-testid="contact-phonework-input"
                className="bg-secondary border-border text-foreground rounded-sm h-8 text-sm" />
            </div>
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                Phone (Private)
                {contact?.apollo_phone?.applied && form.phone_private && (contact.apollo_phone.numbers || []).some(n => n.number === form.phone_private) && (
                  <span data-testid="apollo-phone-badge"
                    className="inline-flex items-center normal-case tracking-normal text-[9px] font-mono border border-orange-500/50 bg-orange-500/10 text-orange-300 rounded px-1 py-0">
                    via Apollo
                  </span>
                )}
              </p>
              <div className="flex gap-1.5">
                <Input value={form.phone_private} onChange={set('phone_private')} data-testid="contact-phoneprivate-input"
                  className="flex-1 bg-secondary border-border text-foreground rounded-sm h-8 text-sm" />
                <Button size="sm" onClick={apolloFindPhone} disabled={phoneBusy}
                  title="Find the mobile number via Apollo.io (~8 credits, takes up to a minute)"
                  data-testid="apollo-phone-btn"
                  className="bg-orange-700 hover:bg-orange-600 text-white rounded-sm h-8 text-[11px] px-2 shrink-0">
                  {phoneBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
                  Apollo
                </Button>
              </div>
            </div>
          </div>
          {phoneResult && (
            <div className="border border-orange-500/30 bg-orange-500/5 rounded-sm p-2.5" data-testid="apollo-phone-box">
              {phoneResult.status === 'pending' ? (
                <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" /> Apollo is looking up the phone number — this can take up to a minute…
                </p>
              ) : (phoneResult.numbers || []).length === 0 ? (
                <p className="text-[11px] text-muted-foreground">Apollo found no phone number for this person.</p>
              ) : (
                <div className="space-y-1.5">
                  {phoneResult.numbers.map((n, i) => (
                    <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`apollo-phone-row-${i}`}>
                      <span className="text-xs text-foreground font-mono">{n.number}</span>
                      <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 ${n.type.includes('mobile') ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-border bg-secondary text-muted-foreground'}`}>
                        {n.type.includes('mobile') ? '📱 Mobile' : n.type || 'other'}
                      </span>
                      {form.phone_private === n.number ? (
                        <span className="ml-auto text-[10px] text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> Applied</span>
                      ) : (
                        <Button size="sm" onClick={() => apolloApplyPhone(n.number)} disabled={phoneApplying}
                          data-testid={`apollo-phone-apply-${i}`}
                          className="ml-auto bg-emerald-700 hover:bg-emerald-600 text-white rounded-sm h-6 text-[10px] px-2">
                          {phoneApplying ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply as private'}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Website</p>
              <Input value={form.website} onChange={set('website')} placeholder="https://…"
                data-testid="contact-website-input"
                className="bg-secondary border-border text-foreground rounded-sm h-8 text-sm" />
            </div>
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                LinkedIn URL
                {contact?.apollo?.linkedin_applied && form.linkedin_url && form.linkedin_url === contact.apollo.linkedin_url && (
                  <span data-testid="apollo-linkedin-badge"
                    className="inline-flex items-center normal-case tracking-normal text-[9px] font-mono border border-orange-500/50 bg-orange-500/10 text-orange-300 rounded px-1 py-0">
                    via Apollo
                  </span>
                )}
              </p>
              <div className="flex gap-1.5">
                <Input value={form.linkedin_url} onChange={set('linkedin_url')} placeholder="https://linkedin.com/in/…"
                  data-testid="contact-linkedin-input"
                  className="flex-1 bg-secondary border-border text-foreground rounded-sm h-8 text-sm" />
                <Button size="sm" onClick={apolloEnrich} disabled={apolloBusy}
                  title="Find the matching LinkedIn profile via Apollo.io" data-testid="apollo-linkedin-btn"
                  className="bg-orange-700 hover:bg-orange-600 text-white rounded-sm h-8 text-[11px] px-2 shrink-0">
                  {apolloBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
                  Apollo
                </Button>
              </div>
            </div>
          </div>
          {apolloResult?.linkedin_url && (
            <div className="border border-orange-500/30 bg-orange-500/5 rounded-sm p-2.5" data-testid="apollo-linkedin-box">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] text-foreground/40 uppercase tracking-wider shrink-0">LinkedIn</span>
                <a href={apolloResult.linkedin_url} target="_blank" rel="noreferrer"
                  className="text-xs text-sky-400 hover:underline truncate min-w-0" data-testid="apollo-linkedin-url">
                  {apolloResult.linkedin_url}
                </a>
                {form.linkedin_url === apolloResult.linkedin_url ? (
                  <span className="ml-auto text-[10px] text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> Applied</span>
                ) : (
                  <Button size="sm" onClick={apolloApplyLinkedinFn} disabled={apolloApplying} data-testid="apollo-linkedin-apply-btn"
                    className="ml-auto bg-emerald-700 hover:bg-emerald-600 text-white rounded-sm h-6 text-[10px] px-2">
                    {apolloApplying ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply profile'}
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Open the profile to verify it is the right person before applying.</p>
            </div>
          )}
          <div>
            <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Notes</p>
            <textarea value={form.notes} onChange={set('notes')} rows={2} maxLength={2000}
              placeholder="e.g. Alter, Besonderheiten, Gesprächsnotizen…" data-testid="contact-notes-input"
              className="w-full text-sm bg-secondary border border-border text-foreground rounded-sm px-2 py-1.5 focus:outline-none resize-y" />
          </div>

          {/* Linked deals / companies */}
          <div className="border border-border rounded-sm p-3 bg-secondary/20" data-testid="contact-linked-deals">
            <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-2">Linked Deals</p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {linkedDeals.length === 0 ? (
                <span className="text-[11px] text-muted-foreground italic">Not linked to any deal — link deals so this contact shows up on the deal page and in the Inbound ranking.</span>
              ) : linkedDeals.map((d, i) => (
                <span key={d.id} data-testid={`linked-deal-chip-${i}`}
                  className="flex items-center gap-1 text-[10px] font-mono border border-violet-600/40 bg-violet-600/10 text-violet-300 rounded px-1.5 py-0.5">
                  <Briefcase className="w-2.5 h-2.5" />
                  {d.name}{d.stage_label ? ` · ${d.stage_label}` : ''}
                  <button onClick={() => removeDeal(d.id)} className="hover:text-red-400" data-testid={`linked-deal-remove-${i}`}>
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <Input value={dealSearch} onChange={e => setDealSearch(e.target.value)}
                placeholder="Search deals to link…" data-testid="deal-link-search"
                className="pl-7 bg-secondary border-border text-foreground rounded-sm h-7 text-xs" />
              {dealResults.length > 0 && (
                <div className="absolute inset-x-0 top-full mt-1 bg-card border border-border rounded-sm shadow-lg z-20 overflow-hidden">
                  {dealResults.map((d, i) => (
                    <button key={d.id} onClick={() => addDeal(d)} data-testid={`deal-link-result-${i}`}
                      className="w-full flex items-center gap-2 px-2.5 py-1.5 hover:bg-secondary/60 text-left">
                      <Briefcase className="w-3 h-3 text-muted-foreground shrink-0" />
                      <span className="text-xs text-foreground truncate">{d.name}</span>
                      {d.code_name && <span className="text-[10px] text-muted-foreground">({d.code_name})</span>}
                      {d.stage_label && <span className="ml-auto text-[9px] font-mono text-muted-foreground border border-border rounded px-1 py-0.5">{d.stage_label}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Photo */}
          <div className="border border-border rounded-sm p-3 bg-secondary/20">
            <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-2">Photo</p>
            {!contact?.id ? (
              <p className="text-xs text-muted-foreground">Save the contact first, then add a photo.</p>
            ) : (
              <div className="flex gap-3 items-start">
                <div className="w-20 h-20 rounded-sm overflow-hidden bg-secondary/60 border border-border shrink-0 flex items-center justify-center">
                  {photoInfo?.has_photo ? (
                    <img src={`${API_BASE}/api/contacts/${contact.id}/photo/file?t=${photoBust}`} alt="contact"
                      className="w-full h-full object-cover" data-testid="contact-photo-preview" />
                  ) : (
                    <UserRound className="w-8 h-8 text-muted-foreground/30" />
                  )}
                </div>
                <div className="flex-1 min-w-0 space-y-1.5">
                  {photoInfo?.has_photo && (
                    <p className={`flex items-center gap-1.5 text-[11px] ${photoInfo.photo_verified ? 'text-emerald-400' : 'text-amber-400'}`} data-testid="contact-photo-status">
                      {photoInfo.photo_verified
                        ? <><BadgeCheck className="w-3.5 h-3.5" /> Verified{photoInfo.photo_verified_by ? ` by ${photoInfo.photo_verified_by}` : ''}</>
                        : '⚠ Not verified yet — any team member or admin can verify'}
                    </p>
                  )}
                  <div className="flex gap-1.5 flex-wrap">
                    <Button size="sm" onClick={findPhotos} disabled={searchingPhotos || photoBusy}
                      data-testid="contact-photo-search-btn"
                      className="bg-sky-700 hover:bg-sky-600 text-white rounded-sm h-7 text-[11px] px-2">
                      {searchingPhotos ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />} Find Photos
                    </Button>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden"
                      onChange={e => uploadPhoto(e.target.files?.[0])} data-testid="contact-photo-file-input" />
                    <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={photoBusy}
                      data-testid="contact-photo-upload-btn"
                      className="border-border text-foreground rounded-sm h-7 text-[11px] px-2">
                      {photoBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />} Upload
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setUrlMode(!urlMode)} disabled={photoBusy}
                      data-testid="contact-photo-url-btn"
                      className="border-border text-foreground rounded-sm h-7 text-[11px] px-2">
                      <Link2 className="w-3 h-3 mr-1" /> From URL
                    </Button>
                    <Button variant="outline" size="sm" onClick={linkedinSearch}
                      data-testid="contact-linkedin-btn"
                      className="border-sky-700/50 text-sky-400 hover:bg-sky-600/10 rounded-sm h-7 text-[11px] px-2">
                      <Linkedin className="w-3 h-3 mr-1" /> Search LinkedIn
                    </Button>
                    {photoInfo?.has_photo && !photoInfo.photo_verified && (
                      <Button size="sm" onClick={verifyPhoto} data-testid="contact-photo-verify-btn"
                        className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-sm h-7 text-[11px] px-2">
                        <BadgeCheck className="w-3 h-3 mr-1" /> Verify
                      </Button>
                    )}
                    {photoInfo?.has_photo && (
                      <Button variant="ghost" size="sm" onClick={deletePhoto} data-testid="contact-photo-delete-btn"
                        className="text-red-400/70 hover:text-red-400 rounded-sm h-7 text-[11px] px-2">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  {urlMode && (
                    <div className="flex gap-1.5">
                      <Input value={photoUrl} onChange={e => setPhotoUrl(e.target.value)}
                        placeholder="https://… (right-click a photo → copy image address)"
                        data-testid="contact-photo-url-input"
                        onKeyDown={e => e.key === 'Enter' && importFromUrl()}
                        className="bg-secondary border-border text-foreground rounded-sm h-7 text-xs" />
                      <Button size="sm" onClick={importFromUrl} disabled={photoBusy}
                        className="bg-blue-700 hover:bg-blue-600 text-white rounded-sm h-7 text-xs px-2">Fetch</Button>
                    </div>
                  )}
                  {candidates !== null && candidates.length > 0 && (
                    <div data-testid="contact-photo-candidates">
                      <p className="text-[10px] text-muted-foreground mb-1">Click the correct photo — it will be saved and must then be verified:</p>
                      <div className="grid grid-cols-4 gap-1.5">
                        {candidates.map((c, i) => (
                          <button key={i} onClick={() => pickCandidate(c)} disabled={photoBusy} title={c.title}
                            data-testid={`photo-candidate-${i}`}
                            className="relative aspect-square rounded-sm overflow-hidden border border-border hover:border-sky-500 transition-colors bg-secondary/40 disabled:opacity-50">
                            <img src={c.thumbnail} alt={c.title} loading="lazy" className="w-full h-full object-cover"
                              onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
                            {c.domain?.includes('linkedin') && (
                              <span className="absolute bottom-0 inset-x-0 bg-sky-700/90 text-white text-[8px] text-center py-0.5">LinkedIn</span>
                            )}
                          </button>
                        ))}
                      </div>
                      <button onClick={() => setCandidates(null)} className="text-[10px] text-muted-foreground hover:text-foreground mt-1">Dismiss suggestions</button>
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground">
                    &ldquo;Find Photos&rdquo; searches the web (incl. LinkedIn) by name + company. Alternatively: &ldquo;Search LinkedIn&rdquo; opens the profile search — right-click the profile photo, copy the image address and paste it via &ldquo;From URL&rdquo;.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Calls & Meetings history */}
          {contact?.id && <EngagementHistory contactId={contact.id} onOpenChange={onOpenChange} />}
        </div>
        {dupMatches?.length > 0 && (
          <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm p-3 space-y-1.5" data-testid="contact-duplicate-warning">
            <p className="text-xs text-amber-300 flex items-center gap-1.5 font-medium">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Ähnlicher Kontakt existiert bereits — nicht dieselbe Person für verschiedene Firmen anschreiben!
            </p>
            {dupMatches.map(m => (
              <div key={m.id} className="flex items-center gap-2 flex-wrap text-[11px] text-foreground" data-testid={`contact-duplicate-match-${m.id}`}>
                <span>
                  {m.name}{m.type ? ` · ${m.type}` : ''}{m.company ? ` · ${m.company}` : ''}
                  {m.reason ? <span className="text-amber-300/90"> — {m.reason}</span> : null}
                  {m.deals?.length ? <span className="text-muted-foreground"> · Deals: {m.deals.join(', ')}</span> : null}
                </span>
                {linkedDeals.length > 0 && (
                  <button onClick={() => linkExisting(m)} disabled={linking === m.id} data-testid={`contact-duplicate-link-${m.id}`}
                    className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors disabled:opacity-50">
                    {linking === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
                    Bestehenden Kontakt mit {linkedDeals.length === 1 ? 'diesem Deal' : `${linkedDeals.length} Deals`} verknüpfen
                  </button>
                )}
              </div>
            ))}
            <p className="text-[10px] text-muted-foreground">Nur „Trotzdem anlegen", wenn es wirklich eine andere Person ist{linkedDeals.length ? ' — sonst den bestehenden Kontakt verknüpfen' : ''}.</p>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="contact-save-btn"
            className={`${dupMatches?.length ? 'bg-amber-600 hover:bg-amber-500' : 'bg-emerald-600 hover:bg-emerald-500'} text-white rounded-sm text-xs h-8 px-4`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />} {dupMatches?.length ? 'Trotzdem anlegen' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
